import { FC, FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Navigate, useNavigate } from "react-router-dom";
import { AuthContainer } from "components/Layout/AuthContainer";
import { StyledFormicError } from "modals/ResetPasswordModal/styles";
import {
  LoginResponseType,
  LoginSteps,
  Messages,
  NotificationSeverity,
} from "helpers/constants";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { authStatusSelector } from "reduxStore/auth/selectors";
import { showNotificationThunk } from "reduxStore/alert/thunks";
import {
  beginTotpSetupThunk,
  confirmMfaThunk,
  selectMfaFactorThunk,
  verifyTotpSetupThunk,
} from "reduxStore/auth/thunks";
import { AdminRoutes } from "config/routes";

const MFA_CODE_STEPS = new Set<string>([
  LoginSteps.CONFIRM_SIGN_IN_WITH_TOTP_CODE,
  LoginSteps.CONFIRM_SIGN_IN_WITH_SMS_CODE,
  LoginSteps.CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE,
]);

/**
 * Completes Cognito MFA challenges that password login may return. Without this
 * screen, MFA-enforced accounts (including admins) could not finish sign-in.
 */
export const MfaChallenge: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const statuses = useAppSelector(authStatusSelector);
  const loginAction = statuses["auth/logInThunk"]?.action as string | undefined;

  const [step, setStep] = useState<string | undefined>(loginAction);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totpSecret, setTotpSecret] = useState<string | null>(null);
  const [totpUri, setTotpUri] = useState<string | null>(null);

  useEffect(() => {
    if (loginAction) {
      setStep(loginAction);
    }
  }, [loginAction]);

  useEffect(() => {
    if (step !== LoginSteps.CONTINUE_SIGN_IN_WITH_TOTP_SETUP) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const details = await dispatch(beginTotpSetupThunk()).unwrap();
        if (cancelled) return;
        setTotpSecret(details.sharedSecret);
        setTotpUri(details.setupUri);
      } catch (err) {
        if (cancelled) return;
        setError(
          typeof err === "string" ? err : "Could not start authenticator setup.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, step]);

  const title = useMemo(() => {
    switch (step) {
      case LoginSteps.CONTINUE_SIGN_IN_WITH_MFA_SELECTION:
        return "Choose a verification method";
      case LoginSteps.CONTINUE_SIGN_IN_WITH_TOTP_SETUP:
        return "Set up authenticator MFA";
      case LoginSteps.CONFIRM_SIGN_IN_WITH_SMS_CODE:
        return "Enter the SMS code";
      case LoginSteps.CONFIRM_SIGN_IN_WITH_TOTP_CODE:
      default:
        return "Enter your authenticator code";
    }
  }, [step]);

  const finishIfSuccess = useCallback(
    (payload: unknown) => {
      if (
        payload &&
        typeof payload === "object" &&
        "type" in payload &&
        (payload as { type: string }).type === LoginResponseType.SUCCESS
      ) {
        window.location.assign(AdminRoutes.Home);
        return true;
      }
      if (
        payload &&
        typeof payload === "object" &&
        "type" in payload &&
        (payload as { type: string }).type === LoginResponseType.ACTION_REQUIRED
      ) {
        const next = (payload as { payload?: { action?: string } }).payload
          ?.action;
        if (next) setStep(next);
        return false;
      }
      return false;
    },
    [],
  );

  const onSelectFactor = async (factor: "TOTP" | "SMS") => {
    setLoading(true);
    setError(null);
    try {
      const result = await dispatch(selectMfaFactorThunk(factor)).unwrap();
      finishIfSuccess(result);
    } catch (err) {
      setError(
        typeof err === "string" ? err : "Could not select MFA method.",
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmitCode = async (event: FormEvent) => {
    event.preventDefault();
    if (!code.trim()) {
      setError(Messages.errorEmptyFields);
      return;
    }
    setLoading(true);
    setError(null);

    if (step === LoginSteps.CONTINUE_SIGN_IN_WITH_TOTP_SETUP) {
      try {
        const result = await dispatch(
          verifyTotpSetupThunk({ code, completeSignIn: true }),
        ).unwrap();
        if (!finishIfSuccess(result)) {
          dispatch(
            showNotificationThunk({
              message: "Authenticator MFA is enabled. Continue sign-in.",
              severity: NotificationSeverity.success,
            }),
          );
        }
      } catch (err) {
        setError(
          typeof err === "string"
            ? err
            : "Authenticator code was not accepted.",
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const result = await dispatch(confirmMfaThunk({ code })).unwrap();
      finishIfSuccess(result);
    } catch (err) {
      setError(
        typeof err === "string" ? err : "Verification code was rejected.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!step) {
    return <Navigate to={AdminRoutes.Login} replace />;
  }

  return (
    <AuthContainer loading={loading} loadingText="Verifying…">
      <Box sx={{ width: "100%", maxWidth: 420 }}>
        <Typography component="h1" variant="h6" gutterBottom>
          {title}
        </Typography>

        {step === LoginSteps.CONTINUE_SIGN_IN_WITH_MFA_SELECTION && (
          <Stack spacing={1.5} mt={2}>
            <Button
              variant="contained"
              disabled={loading}
              onClick={() => void onSelectFactor("TOTP")}
            >
              Authenticator app
            </Button>
            <Button
              variant="outlined"
              disabled={loading}
              onClick={() => void onSelectFactor("SMS")}
            >
              SMS code
            </Button>
          </Stack>
        )}

        {step === LoginSteps.CONTINUE_SIGN_IN_WITH_TOTP_SETUP && (
          <Stack spacing={1.5} mt={2} component="form" onSubmit={onSubmitCode}>
            <Typography variant="body2" color="text.secondary">
              Add VetNebula in your authenticator app using this secret, then
              enter the 6-digit code it shows.
            </Typography>
            {totpSecret && (
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
              >
                {totpSecret}
              </Typography>
            )}
            {totpUri && (
              <Typography variant="caption" color="text.secondary">
                Setup URI: {totpUri}
              </Typography>
            )}
            <TextField
              label="Authenticator code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              autoComplete="one-time-code"
              fullWidth
              required
            />
            <Button type="submit" variant="contained" disabled={loading}>
              Verify and continue
            </Button>
          </Stack>
        )}

        {step && MFA_CODE_STEPS.has(step) && (
          <Box component="form" onSubmit={onSubmitCode} mt={2}>
            <TextField
              label="Verification code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              autoComplete="one-time-code"
              autoFocus
              fullWidth
              required
              margin="normal"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mt: 2 }}
            >
              Verify
            </Button>
          </Box>
        )}

        {error && <StyledFormicError>{error}</StyledFormicError>}

        <Button
          fullWidth
          variant="text"
          sx={{ mt: 2 }}
          disabled={loading}
          onClick={() => navigate(AdminRoutes.Login)}
        >
          Back to sign in
        </Button>
      </Box>
    </AuthContainer>
  );
};

export default MfaChallenge;
